import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, Alert, Chip, Stack, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, LinearProgress, Divider, TextField, Checkbox, FormControlLabel,
  Select, MenuItem, FormControl, InputLabel, Tooltip,
} from '@mui/material';
import {
  EditNote as EditIcon,
  Close as CloseIcon,
  Visibility as PreviewIcon,
  CheckCircle as OkIcon,
  ArrowForward as ArrowIcon,
  ErrorOutline as FailIcon,
  RemoveCircleOutline as SkipIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';

/**
 * In-place bulk-update dialog. Operator has selected N rows in a table;
 * this dialog lets them pick which fields to overwrite, enter the new
 * values, then PREVIEW the old→new diff for every affected row before
 * applying. Diff preview is the key difference vs CIN7's blind update.
 *
 * Props
 *   open               — bool
 *   onClose            — () => void
 *   entityLabel        — singular noun e.g. "supplier"
 *   entityLabelPlural  — defaults to `${entityLabel}s`
 *   selectedRows       — array of full row objects currently selected
 *   editableFields     — [{ field, label?, type?, enum?, multiline? }]
 *                        type: 'string' (default) | 'number' | 'boolean' | 'enum'
 *   identityColumns    — array of field names used to identify each row in the
 *                        preview (default: ['id'])
 *   onSubmit           — async (updates) => { updated?, failed?, errors? }
 *                        updates: [{ id, ...changedFields }]
 */
export default function BulkUpdateDialog({
  open,
  onClose,
  entityLabel,
  entityLabelPlural,
  selectedRows = [],
  editableFields = [],
  identityColumns = ['id'],
  onSubmit,
}) {
  const plural = entityLabelPlural || `${entityLabel}s`;

  // Map of field -> { active, value }
  const [fieldEdits, setFieldEdits] = useState({});
  const [step, setStep] = useState(0); // 0 = pick, 1 = preview, 2 = done
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      const init = {};
      for (const f of editableFields) {
        init[f.field] = { active: false, value: f.type === 'boolean' ? 1 : '' };
      }
      setFieldEdits(init);
      setStep(0);
      setSubmitting(false);
      setResult(null);
    }
  }, [open, editableFields]);

  const activeFields = useMemo(
    () =>
      editableFields.filter((f) => fieldEdits[f.field]?.active),
    [editableFields, fieldEdits]
  );

  const updates = useMemo(() => {
    if (activeFields.length === 0) return [];
    return selectedRows.map((row) => {
      const u = { id: row.id };
      for (const f of activeFields) {
        const v = fieldEdits[f.field].value;
        // In the in-place flow, ticking a field + leaving it blank means the
        // operator wants to clear that field. The backend treats '' as "no-op"
        // (so blank cells in CSV-update don't accidentally blank everything),
        // so we substitute the '__CLEAR__' sentinel for explicit clears. This
        // also enables the enum dropdown's "(clear)" option to actually clear.
        // Numbers/booleans never produce '' so they're unaffected.
        const isEmpty = v === '' || v === null || v === undefined;
        u[f.field] = isEmpty && f.type !== 'number' && f.type !== 'boolean' ? '__CLEAR__' : v;
      }
      return u;
    });
  }, [activeFields, fieldEdits, selectedRows]);

  // Build a per-row "actually changing" map: only rows where at least one
  // active field's new value differs from the current value count as changes.
  const changeStats = useMemo(() => {
    let willChange = 0;
    let unchanged = 0;
    for (const row of selectedRows) {
      const diffs = activeFields.filter((f) => {
        const newV = fieldEdits[f.field].value;
        const oldV = row[f.field];
        // Normalize for comparison
        const a = oldV == null ? '' : String(oldV);
        const b = newV == null ? '' : String(newV);
        return a !== b;
      });
      if (diffs.length > 0) willChange++;
      else unchanged++;
    }
    return { willChange, unchanged };
  }, [activeFields, fieldEdits, selectedRows]);

  const handleToggle = (field) => {
    setFieldEdits((s) => ({
      ...s,
      [field]: { ...s[field], active: !s[field]?.active },
    }));
  };
  const handleValue = (field, value) => {
    setFieldEdits((s) => ({
      ...s,
      [field]: { ...s[field], value },
    }));
  };

  const handleSubmit = async () => {
    if (!onSubmit || updates.length === 0) return;
    setSubmitting(true);
    setResult(null);
    try {
      const res = await onSubmit(updates);
      setResult({ ok: true, ...res });
      setStep(2);
    } catch (err) {
      setResult({ ok: false, error: err?.response?.data?.error || err.message || 'Update failed' });
    } finally {
      setSubmitting(false);
    }
  };

  const renderFieldInput = (f) => {
    const cur = fieldEdits[f.field];
    if (!cur?.active) return null;
    if (f.type === 'boolean') {
      return (
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select value={cur.value} onChange={(e) => handleValue(f.field, e.target.value)}>
            <MenuItem value={1}>Yes / Active</MenuItem>
            <MenuItem value={0}>No / Inactive</MenuItem>
          </Select>
        </FormControl>
      );
    }
    if (f.type === 'enum' || f.enum) {
      const opts = f.enum || [];
      return (
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            value={cur.value || ''}
            onChange={(e) => handleValue(f.field, e.target.value)}
            displayEmpty
          >
            <MenuItem value=""><em>(clear)</em></MenuItem>
            {opts.map((o) => (
              <MenuItem key={o} value={o}>{o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    if (f.type === 'number') {
      return (
        <TextField
          size="small"
          type="number"
          value={cur.value}
          onChange={(e) => handleValue(f.field, e.target.value)}
          sx={{ minWidth: 160 }}
        />
      );
    }
    return (
      <TextField
        size="small"
        value={cur.value}
        onChange={(e) => handleValue(f.field, e.target.value)}
        multiline={!!f.multiline}
        rows={f.multiline ? 2 : 1}
        sx={{ minWidth: 260 }}
        placeholder={f.placeholder || ''}
      />
    );
  };

  const renderIdentity = (row) =>
    identityColumns
      .map((c) => row[c])
      .filter((v) => v !== undefined && v !== null && v !== '')
      .join(' · ');

  const previewRowsToShow = useMemo(() => selectedRows.slice(0, 50), [selectedRows]);
  const overflow = Math.max(0, selectedRows.length - previewRowsToShow.length);

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EditIcon color="primary" />
        <Box flexGrow={1}>
          Bulk update {plural}
          <Typography variant="caption" display="block" color="text.secondary">
            {selectedRows.length} {selectedRows.length === 1 ? entityLabel : plural} selected ·
            {' '}
            {step === 0 && 'Pick fields to overwrite'}
            {step === 1 && 'Review changes before applying'}
            {step === 2 && 'Done'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={submitting} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {step === 0 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Tick a field to enable it, then enter the value you want applied to every selected{' '}
              {entityLabel}. Blank values are kept as blanks (use this to clear a field). Fields
              you don't tick are not touched.
            </Alert>
            <Stack spacing={1.5}>
              {editableFields.map((f) => (
                <Stack
                  key={f.field}
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: fieldEdits[f.field]?.active ? 'primary.main' : 'divider',
                    bgcolor: fieldEdits[f.field]?.active ? 'action.hover' : 'transparent',
                    borderRadius: 1,
                    transition: 'all 0.15s',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!fieldEdits[f.field]?.active}
                        onChange={() => handleToggle(f.field)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {f.label || f.field}
                        </Typography>
                        {f.help && (
                          <Typography variant="caption" color="text.secondary">{f.help}</Typography>
                        )}
                      </Box>
                    }
                    sx={{ minWidth: 240, mr: 0 }}
                  />
                  <Box flexGrow={1}>{renderFieldInput(f)}</Box>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        {step === 1 && (
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5} flexWrap="wrap">
              <Chip
                size="small"
                color="primary"
                icon={<PreviewIcon fontSize="inherit" />}
                label={`${changeStats.willChange} will change`}
              />
              {changeStats.unchanged > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${changeStats.unchanged} already match (will be no-ops)`}
                />
              )}
              <Box flexGrow={1} />
              <Typography variant="caption" color="text.secondary">
                Updating: {activeFields.map((f) => f.label || f.field).join(', ')}
              </Typography>
            </Stack>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 50 }}>#</TableCell>
                    <TableCell>{entityLabel}</TableCell>
                    {activeFields.map((f) => (
                      <TableCell key={f.field}>{f.label || f.field}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewRowsToShow.map((row, i) => (
                    <TableRow key={row.id || i} hover>
                      <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {renderIdentity(row)}
                        </Typography>
                      </TableCell>
                      {activeFields.map((f) => {
                        const newV = fieldEdits[f.field].value;
                        const oldV = row[f.field];
                        const a = oldV == null ? '' : String(oldV);
                        const b = newV == null ? '' : String(newV);
                        const changed = a !== b;
                        return (
                          <TableCell key={f.field} sx={{ fontSize: 12, color: changed ? 'inherit' : 'text.disabled' }}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography variant="caption" sx={{ textDecoration: changed ? 'line-through' : 'none', color: 'text.disabled' }}>
                                {a || '∅'}
                              </Typography>
                              <ArrowIcon fontSize="inherit" sx={{ color: changed ? 'primary.main' : 'text.disabled' }} />
                              <Typography variant="caption" fontWeight={changed ? 600 : 400} color={changed ? 'primary.main' : 'text.disabled'}>
                                {b || '∅'}
                              </Typography>
                            </Stack>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                  {overflow > 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={2 + activeFields.length}
                        align="center"
                        sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                      >
                        …and {overflow} more row{overflow === 1 ? '' : 's'} below
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {submitting && <LinearProgress sx={{ mt: 1.5 }} />}
            {result && !result.ok && (
              <Alert severity="error" sx={{ mt: 1.5 }}>{result.error}</Alert>
            )}
          </Box>
        )}

        {step === 2 && result?.ok && (() => {
          // Build per-row outcome map. Backend returned `{ updated, failed, errors[{id, error}] }`.
          // Rows in `errors` are the failures; everything else in selectedRows
          // succeeded (or was a no-op because nothing changed). We render the
          // same diff table from Step 1 with an outcome column on the left.
          const errorById = new Map();
          for (const e of (result.errors || [])) {
            if (e.id !== undefined) errorById.set(String(e.id), e.error || e.message || 'Failed');
          }
          const rowOutcomes = selectedRows.map((row) => {
            const err = errorById.get(String(row.id));
            const changedFields = activeFields.filter((f) => {
              const newV = fieldEdits[f.field].value;
              const oldV = row[f.field];
              return (oldV == null ? '' : String(oldV)) !== (newV == null ? '' : String(newV));
            });
            if (err) return { row, status: 'failed', error: err, changedFields };
            if (changedFields.length === 0) return { row, status: 'noop', changedFields };
            return { row, status: 'updated', changedFields };
          });
          const counts = {
            updated: rowOutcomes.filter((o) => o.status === 'updated').length,
            noop: rowOutcomes.filter((o) => o.status === 'noop').length,
            failed: rowOutcomes.filter((o) => o.status === 'failed').length,
          };
          const visibleOutcomes = rowOutcomes.slice(0, 200);
          const hiddenCount = rowOutcomes.length - visibleOutcomes.length;

          // Download a CSV record of every applied change so the operator has
          // an audit-friendly artifact (especially useful for compliance flows).
          const handleDownloadSummary = () => {
            const cols = ['outcome', ...identityColumns, 'field', 'old_value', 'new_value', 'error'];
            const escape = (v) => {
              if (v === null || v === undefined) return '';
              const s = String(v);
              return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
            };
            const lines = [cols.join(',')];
            for (const o of rowOutcomes) {
              const idCells = identityColumns.map((c) => o.row[c]);
              if (o.status === 'failed') {
                lines.push([escape('failed'), ...idCells.map(escape), '', '', '', escape(o.error)].join(','));
                continue;
              }
              if (o.changedFields.length === 0) {
                lines.push([escape('no-op'), ...idCells.map(escape), '', '', '', ''].join(','));
                continue;
              }
              for (const f of o.changedFields) {
                lines.push([
                  escape('updated'),
                  ...idCells.map(escape),
                  escape(f.label || f.field),
                  escape(o.row[f.field]),
                  escape(fieldEdits[f.field].value),
                  '',
                ].join(','));
              }
            }
            const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${plural}-bulk-update-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          };

          return (
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
                <OkIcon color="success" sx={{ fontSize: 36 }} />
                <Box flexGrow={1}>
                  <Typography variant="h6">
                    {counts.updated > 0
                      ? `Updated ${counts.updated} ${counts.updated === 1 ? entityLabel : plural}`
                      : 'No changes applied'}
                  </Typography>
                  <Stack direction="row" spacing={0.75} mt={0.5} flexWrap="wrap">
                    {counts.updated > 0 && (
                      <Chip size="small" color="success" variant="outlined"
                        icon={<OkIcon fontSize="inherit" />} label={`${counts.updated} updated`} />
                    )}
                    {counts.noop > 0 && (
                      <Chip size="small" variant="outlined"
                        icon={<SkipIcon fontSize="inherit" />} label={`${counts.noop} no-op (already matched)`} />
                    )}
                    {counts.failed > 0 && (
                      <Chip size="small" color="error" variant="outlined"
                        icon={<FailIcon fontSize="inherit" />} label={`${counts.failed} failed`} />
                    )}
                  </Stack>
                </Box>
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadSummary}
                  variant="outlined"
                >
                  Download summary
                </Button>
              </Stack>

              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Per-row outcome — each cell shows old → new for the fields you updated.
              </Typography>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 32 }}></TableCell>
                      <TableCell sx={{ width: 60 }}>#</TableCell>
                      <TableCell>{entityLabel}</TableCell>
                      {activeFields.map((f) => (
                        <TableCell key={f.field}>{f.label || f.field}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleOutcomes.map((o, i) => (
                      <TableRow key={o.row.id || i} hover sx={{
                        bgcolor: o.status === 'failed' ? 'error.lighter'
                          : o.status === 'noop' ? 'action.hover'
                          : 'inherit',
                      }}>
                        <TableCell sx={{ width: 32 }}>
                          {o.status === 'updated' && <OkIcon color="success" fontSize="small" />}
                          {o.status === 'noop' && <SkipIcon color="disabled" fontSize="small" />}
                          {o.status === 'failed' && (
                            <Tooltip title={o.error}>
                              <FailIcon color="error" fontSize="small" />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {renderIdentity(o.row)}
                          </Typography>
                          {o.status === 'failed' && (
                            <Typography variant="caption" color="error.main" component="div">
                              {o.error}
                            </Typography>
                          )}
                        </TableCell>
                        {activeFields.map((f) => {
                          const newV = fieldEdits[f.field].value;
                          const oldV = o.row[f.field];
                          const a = oldV == null ? '' : String(oldV);
                          const b = newV == null ? '' : String(newV);
                          const changed = a !== b && o.status === 'updated';
                          return (
                            <TableCell key={f.field} sx={{ fontSize: 12, color: changed ? 'inherit' : 'text.disabled' }}>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography variant="caption" sx={{
                                  textDecoration: changed ? 'line-through' : 'none',
                                  color: 'text.disabled',
                                }}>
                                  {a || '∅'}
                                </Typography>
                                <ArrowIcon fontSize="inherit" sx={{
                                  color: changed ? 'success.main' : 'text.disabled',
                                }} />
                                <Typography variant="caption" fontWeight={changed ? 600 : 400}
                                  color={changed ? 'success.main' : 'text.disabled'}
                                >
                                  {o.status === 'failed' ? a : (b || '∅')}
                                </Typography>
                              </Stack>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                    {hiddenCount > 0 && (
                      <TableRow>
                        <TableCell colSpan={3 + activeFields.length} align="center" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                          …and {hiddenCount} more row{hiddenCount === 1 ? '' : 's'} (download the summary CSV to see them all)
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        })()}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {step === 0 && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => setStep(1)}
              disabled={activeFields.length === 0 || selectedRows.length === 0}
            >
              Preview {selectedRows.length} {selectedRows.length === 1 ? entityLabel : plural} →
            </Button>
          </>
        )}
        {step === 1 && (
          <>
            <Button onClick={() => setStep(0)} disabled={submitting}>← Back</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={submitting || changeStats.willChange === 0}
            >
              {submitting ? 'Applying…' : `Apply to ${changeStats.willChange} ${changeStats.willChange === 1 ? entityLabel : plural}`}
            </Button>
          </>
        )}
        {step === 2 && (
          <Button onClick={onClose} variant="contained">Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
